import { useCallback, useState } from 'react';
import PropType from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row, Divider, Checkbox } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { BaseButton } from 'components/UI/BaseButton';
import {
  TRANSLATION_LANGUAGE_LIST,
  TRANSLATION_NAMESPACES
} from 'util/translation/constant';
import { BaseSelect } from 'components/UI/BaseSelect';

export const OperatorSelectFields = ({
  responsibilities,
  isLoading,
  setFieldsValue
}) => {
  const { t } = useTranslation(TRANSLATION_NAMESPACES.OPERATOR);
  const { Option } = BaseSelect;

  const [checkedList, setCheckedList] = useState(
    responsibilities.map((responsibility) => ({
      name: responsibility.name,
      value: responsibility.value
    }))
  );
  const [indeterminate, setIndeterminate] = useState(
    responsibilities.filter((x) => x.value).length > 0 &&
      responsibilities.filter((x) => x.value).length < responsibilities.length
  );
  const [checkAll, setCheckAll] = useState(
    responsibilities.every((responsibility) => responsibility.value === true)
  );

  const handleChange = useCallback(
    (e) => {
      const { id, checked } = e.target;
      const newCheckList = [
        ...checkedList.filter((item) => item.name !== id),
        { name: id, value: checked }
      ];
      setCheckedList(newCheckList);
      const checkListValues = newCheckList.filter((x) => x.value);
      setIndeterminate(
        checkListValues.length > 0 &&
          checkListValues.length < responsibilities.length
      );
      setCheckAll(checkListValues.length === responsibilities.length);
    },
    [checkedList, responsibilities.length]
  );

  const handleSelectAll = useCallback(
    (e) => {
      const { checked } = e.target;
      setIndeterminate(false);
      setCheckAll(checked);
      responsibilities.forEach((responsibility) => {
        setFieldsValue({ [responsibility.name]: checked });
      });
      checkedList.forEach((responsibility) => {
        responsibility.value = checked;
      });
    },
    [checkedList, responsibilities, setFieldsValue]
  );

  return (
    <>
      <Divider className="custom-divider" orientation="left" plain>
        {t('operatorForm.responsibilitiesHint')}
        <Checkbox
          className="custom-checkbox ml-4"
          indeterminate={indeterminate}
          onChange={handleSelectAll}
          checked={checkAll}
          data-testid="check-all">
          {t('operatorForm.selectAll')}
        </Checkbox>
      </Divider>
      <div className="operator-checklist" data-testid="responsibility-checkbox">
        {responsibilities?.map((task) => (
          <Form.Item key={task.id} name={task.name} valuePropName="checked">
            <Checkbox className="custom-checkbox" onChange={handleChange}>
              {task.label}
            </Checkbox>
          </Form.Item>
        ))}
      </div>
      <div className="mt-8 mb-4">
        <span>{t('operatorForm.languageHint')}</span>
      </div>
      <div className="operator-actions">
        <Row justify="space-between">
          <Col md={5} lg={5} span={10}>
            <Form.Item name="language">
              <BaseSelect className="custom-select">
                {TRANSLATION_LANGUAGE_LIST.map((lang) => {
                  return (
                    <Option key={lang.value} value={lang.value}>
                      {lang.name}
                    </Option>
                  );
                })}
              </BaseSelect>
            </Form.Item>
          </Col>
          <Col md={5} lg={5} span={10} align="end">
            <BaseButton
              className="base-yellow-btn save-btn"
              type="primary"
              loading={isLoading}
              htmlType="submit">
              {t('operatorForm.submitButton')}
              <SaveOutlined />
            </BaseButton>
          </Col>
        </Row>
      </div>
    </>
  );
};

OperatorSelectFields.propTypes = {
  responsibilities: PropType.arrayOf(
    PropType.shape({
      id: PropType.number,
      label: PropType.string,
      name: PropType.string,
      value: PropType.bool
    })
  ).isRequired,
  isLoading: PropType.bool.isRequired,
  setFieldsValue: PropType.func.isRequired
};
